import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Star, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { KINDS } from "@/lib/kinds";
import { PageHeader, SearchInput } from "@/components/common/Layout";
import { EmptyState, ErrorState, LoadingGrid } from "@/components/common/States";
import { GroupIcon } from "@/components/common/Visuals";
import { GroupFormDialog } from "@/components/groups/GroupFormDialog";
import { normalizeText } from "@/lib/text";
import { plural } from "@/lib/format";
import { cn } from "@/lib/utils";

function GroupCard({ group, path }) {
  return (
    <Link to={`${path}/${group.id}`} data-testid={`group-card-${group.id}`} className="lm-card lm-card-hover relative flex flex-col overflow-hidden p-6">
      <div className="absolute inset-x-0 top-0 h-1.5" style={{ background: group.color || "#A4247F" }} />
      <div className="flex items-start justify-between gap-3">
        <span className="h-12 w-12 rounded-2xl"><GroupIcon group={group} box className="h-5 w-5" /></span>
        <div className="flex gap-1.5">
          {group.featured && (
            <span data-testid={`group-featured-badge-${group.id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700">
              <Star className="h-3 w-3 fill-current" /> Mis en avant
            </span>
          )}
          {!group.active && <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[11px] font-bold text-stone-500">Archivé</span>}
        </div>
      </div>
      <h3 className="mt-4 text-lg font-bold">{group.name}</h3>
      {group.type && group.type !== "Club" && group.type !== "Commission" && (
        <div className="mt-1 text-xs font-semibold uppercase tracking-wider text-primary/80">{group.type}</div>
      )}
      {group.description && <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{group.description}</p>}
      <div className="mt-auto flex items-center justify-between gap-3 pt-5 text-sm">
        <span className="inline-flex items-center gap-1.5 font-semibold" style={{ color: group.color || undefined }}>
          <Users className="h-4 w-4" /> {plural(group.members_count, "membre", "membres")}
        </span>
        {/* Ordre strict : 1. Responsable(s) La Mêlée, 2. Animateur(s). Masquer libellé vide. Pas de Référent CA */}
        {group.managers?.length > 0 ? (
          <span className="truncate text-xs text-stone-600">
            Resp. La Mêlée : {group.managers.map((m) => m.name).join(", ")}
          </span>
        ) : group.animators?.length > 0 ? (
          <span className="truncate text-xs text-stone-600">
            Animateur{group.animators.length > 1 ? "s" : ""} : {group.animators.map((a) => a.full_name || a.name).join(", ")}
          </span>
        ) : null}
      </div>
    </Link>
  );
}

export default function GroupsPage({ kind }) {
  const meta = KINDS[kind];
  const { isAdmin, isAuthed } = useAuth();
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [creating, setCreating] = useState(false);
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["groups", kind, isAuthed],
    queryFn: async () => (await api.get(`/groups/${kind}`)).data,
  });

  const allItems = (data || []).filter((g) => normalizeText(`${g.name} ${g.description || ""} ${g.type || ""}`).includes(normalizeText(q)));

  const mainCount = allItems.filter((g) => g.type === "Commission").length;
  const subgroupCount = allItems.filter((g) => g.type === "Sous-groupe").length;
  const workingCount = allItems.filter((g) => g.type === "Groupe de travail").length;

  const items = allItems.filter((g) => {
    if (kind !== "commissions" || tab === "all") return true;
    if (tab === "main") return g.type === "Commission";
    if (tab === "subgroup") return g.type === "Sous-groupe";
    if (tab === "working") return g.type === "Groupe de travail";
    return true;
  });

  return (
    <div className="lm-container pb-20">
      <PageHeader eyebrow="Communautés" title={meta.label} description={meta.intro} icon={meta.icon} color="#A4247F"
        actions={isAdmin && (
          <Button className="rounded-full px-5 font-semibold" onClick={() => setCreating(true)} data-testid={`create-${kind}-button`}>
            <Plus className="h-4 w-4" /> {meta.newLabel}
          </Button>
        )} />
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <SearchInput className="max-w-md flex-1" value={q} onChange={setQ} testId={`${kind}-search-input`} placeholder={`Rechercher parmi les ${meta.label.toLowerCase()}…`} />
      </div>

      {kind === "commissions" && (
        <div className="my-5 flex flex-wrap items-center gap-2" data-testid="commission-tabs">
          <button
            type="button"
            onClick={() => setTab("all")}
            data-testid="tab-all"
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all",
              tab === "all" ? "bg-primary text-white shadow-sm" : "bg-stone-100 text-stone-600 hover:bg-stone-200/70"
            )}
          >
            Toutes ({allItems.length})
          </button>
          <button
            type="button"
            onClick={() => setTab("main")}
            data-testid="tab-main"
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all",
              tab === "main" ? "bg-primary text-white shadow-sm" : "bg-stone-100 text-stone-600 hover:bg-stone-200/70"
            )}
          >
            Commissions principales ({mainCount})
          </button>
          <button
            type="button"
            onClick={() => setTab("subgroup")}
            data-testid="tab-subgroup"
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all",
              tab === "subgroup" ? "bg-primary text-white shadow-sm" : "bg-stone-100 text-stone-600 hover:bg-stone-200/70"
            )}
          >
            Sous-groupes ({subgroupCount})
          </button>
          <button
            type="button"
            onClick={() => setTab("working")}
            data-testid="tab-working"
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all",
              tab === "working" ? "bg-primary text-white shadow-sm" : "bg-stone-100 text-stone-600 hover:bg-stone-200/70"
            )}
          >
            Groupes de travail ({workingCount})
          </button>
        </div>
      )}

      {isLoading ? <LoadingGrid /> : isError ? <ErrorState onRetry={refetch} /> : !items.length ? (
        <EmptyState description={q ? "Aucun élément ne correspond à votre recherche." : "Aucun élément pour le moment."} />
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 mt-4" data-testid={`${kind}-grid`}>
          {items.map((g) => <GroupCard key={g.id} group={g} path={meta.path} />)}
        </div>
      )}
      {isAdmin && <GroupFormDialog kind={kind} open={creating} onOpenChange={setCreating} />}
    </div>
  );
}
